import React, { useEffect, useState, useContext } from 'react'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import Table from 'components/Table'
import {usePagination} from 'hooks/usePagination'
import ItemsPerPageDropdown from 'components/ItemsPerPageDropdown'
import Pagination from 'features/Pagination'
import { seasonalDistribution } from 'constants/seasonalDistribution'
import { SiteContext } from 'providers/site'
import { makeCalculationRow, makeCalculationsColumns } from './calculationTable'
import { makeCalculationRowEco, makeCalculationsColumnsEco } from './ecosystemTable'
import { makeCalculationsRowsOtherCriteria, makeCalculationsColumnsOtherCriteria } from './otherCriteriaTable'
import { StyledCalculationTable, StyledCalculationTableEco, SpeciesPaginationWrapper } from './style'
import { requestCalculationData, requestEcoSystemCalculator, requestTreatmentData } from 'api/site'
import AssessmentTitle from 'components/AssessmentTitle'
import {
  makeCalculationsColumnsCongregationCriteria,
  makeCalculationsRowsCongregationCriteria
} from './congregationTable'
import { SITE_STATUS_DRAFT_ID, SITE_STATUS_NOMINATED_ID, SITE_STATUS_PROPOSED_ID, SITE_STATUS_REVIEWED_BY_RFP_ID } from 'constants/siteStatuses'
import { canViewSensitive } from 'utils/rolePermissions'
import { ProfileContext } from 'providers/profile'
import { getCalculationCongregations } from 'api/congregations'
import { showErrorToast } from 'utils/toast'
import { useIBAAccess } from 'hooks/useIBAAccess'

function compareStrings (a, b) {
  if (a < b) {
    return -1
  }
  if (a > b) {
    return 1
  }
  return 0
}

function formatKbaCriteriasJoin (kbaCriterias) {
  if (!kbaCriterias?.length) {
    return '-'
  }
  return [...kbaCriterias].sort(compareStrings).join(', ')
}

function getSeasonFromDistribution (seasonalDistributionList, seasonalDistributionId) {
  if (!seasonalDistributionId) {
    return { name: '-' }
  }
  return seasonalDistributionList?.find((element) => element.id === Number(seasonalDistributionId)) || { name: '-' }
}

function formatIbaCriteriasForSite (site, item, canSeeSiteIBACriteria, shouldShowSiteIBAData) {
  if (!canSeeSiteIBACriteria(site)) {
    return null
  }
  let joined = null
  if (item?.ibaCriterias?.length > 0) {
    joined = [...item.ibaCriterias].sort(compareStrings).join(', ')
  }
  return shouldShowSiteIBAData(site, joined, 'sitesManagement')
}

function normalizeBooleanLikeValue (value) {
  if (value === true || value === 1 || value === '1' || value === 'true') {
    return true
  }
  if (value === false || value === 0 || value === '0' || value === 'false' || value == null) {
    return false
  }
  return Boolean(value)
}

function normalizeTriggerReasonIds (triggerReasonId) {
  if (Array.isArray(triggerReasonId)) {
    return triggerReasonId.map((reasonId) => Number(reasonId)).filter((reasonId) => !Number.isNaN(reasonId))
  }
  if (triggerReasonId == null) {
    return []
  }

  const singleReasonId = Number(triggerReasonId)
  return Number.isNaN(singleReasonId) ? [] : [singleReasonId]
}

function isKbaDelistedByReason (triggerReasonId) {
  const normalizedReasonIds = normalizeTriggerReasonIds(triggerReasonId)
  if (!normalizedReasonIds.length) {
    return false
  }
  return !normalizedReasonIds.includes(1)
}

const Calculations = () => {
  const [calculationData, setCalculationData] = useState([])
  const [calculationDataEco, setCalculationDataEco] = useState([])
  const [congregationData, setCongregationData] = useState([])
  const [treatmentData, setTreatmentData] = useState({})
  const { site, isEditable } = useContext(SiteContext)
  const { profile } = useContext(ProfileContext)
  const { canSeeSiteIBACriteria, shouldShowSiteIBAData, canSeeSiteIBACongregations, canSeeIBACriteria } = useIBAAccess()
  const defaultPaginationState = {
    totalNumber: 0,
    resultsPerPage: 12,
    totalPages: 1,
    currentPage: 1,
    hasNext: true,
    hasPrevious: false
  }
  const { pagination, setPagination, selectPage, nextPage, prevPage, nextSection, prevSection, changeResultsPerPage } = usePagination(defaultPaginationState)
  
  const disabled = site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID)
  
  const showCongregations = canSeeSiteIBACongregations(site) || (site.SiteStatusId === SITE_STATUS_PROPOSED_ID || site.SiteStatusId === SITE_STATUS_NOMINATED_ID || site.SiteStatusId === SITE_STATUS_REVIEWED_BY_RFP_ID || canViewSensitive(profile.RoleId))

  const getCongregationsData = async () => {
    try {
      const response = await getCalculationCongregations(site.id, site.historyId)

      if(response.error) {
        return showErrorToast(response.error)
      }

      setCongregationData(response)
    } catch (e) {
      showErrorToast(e)
    }
  }

  const getCalculationData = () => {

    requestCalculationData(site.id, site.historyId, {resultsPerPage: pagination.resultsPerPage, currentPage: pagination.currentPage}).then((data) => {
      setPagination(data.pagination || {})
      return setCalculationData(data.parsed.map((item) => {
        const isIbaDisqualified = normalizeBooleanLikeValue(
          item.disqualified_iba_trigger !== undefined
            ? item.disqualified_iba_trigger
            : (item.ibaDisqualified !== undefined ? item.ibaDisqualified : item.disqualifiedIbaTrigger)
        )
        const isKbaDelisted = isKbaDelistedByReason(item.triggerReasonId)

        return {
          taxonGroup: (item?.taxonomicGroup ? item?.taxonomicGroup : '-'),
          scientificName: (item?.scientificName ? item?.scientificName : '-'),
          commonName: (item.specieName ? item.specieName : '-'),
          iucnStatus: (item?.RedListCategoryCode ? item?.RedListCategoryCode : '-'),
          reproductiveUnits: (item?.minReproductiveUnits ? item?.minReproductiveUnits : '-'),
          population: item.globalPopulation ? Number(item.globalPopulation).toFixed(2) : '-',
          assessmentParameter: item.SiteAssesmentParameterTypeId?.[0],
          kbaCriteria: formatKbaCriteriasJoin(item?.kbaCriterias),
          season: getSeasonFromDistribution(seasonalDistribution, item.SeasonalDistributionId),
          triggerReasonId: item.triggerReasonId,
          siteAssesmentParameterTypeId: item.SiteAssesmentParameterTypeId,
          disqualifiedIbaTrigger: isIbaDisqualified,
          isIbaDisqualified,
          isKbaDelisted,
          ibaCriterias: formatIbaCriteriasForSite(site, item, canSeeSiteIBACriteria, shouldShowSiteIBAData)
        }
      }))
    }
    )
  }

  const getCalculationDataEco = () => {

    requestEcoSystemCalculator(site.id, site.historyId).then((data) => {
      return setCalculationDataEco(data.map((item) => {
        return {
          ecosystemName: (item?.name ? item?.name : '-'),
          iucnStatus: (item?.EcoSystemRedListId ? item?.EcoSystemRedListId : '-'),
          population: item?.globalPopulation,
          kbaCriteria: (item?.kbaCriterias.length ? item?.kbaCriterias.join(', ') : '-'),
          triggerReasonId: item.triggerReasonId
        }
      }))
    }
    )
  }

  const getTreatmentData = () => {
    requestTreatmentData(site.id, site.historyId).then(setTreatmentData)
  }

  useEffect(() => {
    getCalculationData()
    getCalculationDataEco()
    getTreatmentData()
    getCongregationsData()
  }, [pagination?.currentPage,
    pagination?.resultsPerPage])

  const rows = makeCalculationRow(calculationData, site, disabled, canSeeSiteIBACriteria, shouldShowSiteIBAData)
  const columns = makeCalculationsColumns(canSeeSiteIBACriteria(site))

  const rowsEco = makeCalculationRowEco(calculationDataEco, site)
  const columnsEco = makeCalculationsColumnsEco()

  const rowsOtherCriteria = makeCalculationsRowsOtherCriteria(treatmentData)
  const columnsOtherCriteria = makeCalculationsColumnsOtherCriteria()

  const rowsCongregationCriteria = makeCalculationsRowsCongregationCriteria(congregationData)
  const columnsCongregationCriteria = makeCalculationsColumnsCongregationCriteria(canSeeIBACriteria)

  return (
    <Container>
      <AssessmentTitle
        titleId='speciesDetails'
        subTitleId='calculationsSubTittle'
      />
      <Row>
        <Col md={12}>
          <StyledCalculationTable>
            <Table
              rows={rows}
              columns={columns}
              dataTest="calculations-table"
              noResultMessage="noSpeciesSelected"
              noClearFilter={true}
              height={pagination.totalNumber < 8 ? 'auto' : ''}
            />
          </StyledCalculationTable>
          {pagination.totalNumber > 8 && (
            <SpeciesPaginationWrapper>
              <ItemsPerPageDropdown
                currentPage={pagination.currentPage}
                resultsPerPage={pagination.resultsPerPage}
                totalNumber={pagination.totalNumber}
                onChange={changeResultsPerPage}
                dataTest='sites-items-per-page'
              />
              {pagination.totalPages > 1 && (
                <Pagination
                  activePages={pagination.currentPage}
                  numberOfPages={pagination.totalPages}
                  nextPage={nextPage}
                  prevPage={prevPage}
                  nextSection={nextSection}
                  prevSection={prevSection}
                  selectPage={selectPage}
                  dataTest='page-number'
                />
              )}
            </SpeciesPaginationWrapper>
          )}
        </Col>
      </Row>
      <AssessmentTitle
        titleId='ecosystemDataTable'
        subTitleId='ecosystemDataTableSubTitle'
        marginTop='64px'
      />
      <Row>
        <Col md={12}>
          <StyledCalculationTableEco>
            <Table
              rows={rowsEco}
              columns={columnsEco}
              dataTest="calculations-table"
              noResultMessage="noEcosystemsSelected"
              noClearFilter={true}
            />
          </StyledCalculationTableEco>
        </Col>
      </Row>
      <AssessmentTitle
        titleId='otherCriteriaDataTable'
        subTitleId='otherCriteriaDataTableDescription'
        marginTop='64px'
      />
      <Row>
        <Col md={12}>
          <StyledCalculationTableEco>
            <Table
              rows={rowsOtherCriteria}
              columns={columnsOtherCriteria}
              dataTest="calculations-table"
              noResultMessage="noEcosystemsSelected"
              noClearFilter={true}
            />
          </StyledCalculationTableEco>
        </Col>
      </Row>
      {showCongregations && (
        <>
          <AssessmentTitle
            titleId='congregationTable'
            subTitleId='congregationDataTableSubTitle'
            marginTop='64px'
          />
          <Row>
            <Col md={12}>
              <StyledCalculationTableEco>
                <Table
                  rows={rowsCongregationCriteria}
                  columns={columnsCongregationCriteria}
                  dataTest="congregation-table"
                  noResultMessage="noCongregationSelected"
                  noClearFilter={true}
                />
              </StyledCalculationTableEco>
            </Col>
          </Row>
        </>
      )}
    </Container>
  )
}

export default Calculations